import { useEffect, useState } from "react";
import PlacesAutocomplete, {
  geocodeByAddress,
} from "react-places-autocomplete";
import { formatAddress } from "./formatAddress";
import "./style.scss";
import { useLoadScript } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";

export type CountryCode = "EG";
export type CountryMap = {
  [key in string]?: string;
};

type Props = {
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onSelectData?: (obj?: any) => void;
  value?: string;
  placeholder?: string;
  countryCode?: CountryCode[];
  countryMap?: CountryMap;
  onFocus?: () => void;
  readOnly?: boolean;
  autoFocus?: boolean;
};

export const AutoCompleteSearch = ({
  onChange,
  value,
  onSelectData,
  placeholder,
  onBlur,
  onFocus,
  countryCode = ["EG"],
  countryMap = {
    "United States": "United States of America",
    Canada: "Canada",
  },
  readOnly,
  autoFocus,
}: Props) => {
  const { t } = useTranslation();
  const [valueState, setValueState] = useState<string>();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_URL,
    libraries: ["places"],
  });

  const searchOptions = {
    types: ["address"],
    componentRestrictions: { country: countryCode },
    fields: ["address_component"],
  };

  const handleSelect = (address: string) => {
    geocodeByAddress(address)
      .then((results) => {
        const addressInfo = formatAddress({
          results,
        });

        addressInfo.countryLongName =
          countryMap[addressInfo.countryLongName!] ||
          addressInfo.countryLongName;
        setValueState(addressInfo?.resultsAddress);
        onChange?.(addressInfo?.resultsAddress as string);
        onSelectData?.({
          ...addressInfo,
          location: {
            lat: results?.[0]?.geometry?.location.lat(),
            lng: results?.[0]?.geometry?.location.lng(),
          },
        });
      })
      .catch((error) => console.error("Error", error));
  };

  const onError = (status: string, clearSuggestions: () => void) => {
    console.log("Google Maps API returned error with status: ", status);
    clearSuggestions();
  };

  useEffect(() => {
    setValueState((typeof value === "string" ? value : undefined)?.trimStart());
  }, [value]);

  if (!isLoaded) return null;

  return (
    <div className="auto-complete-wrapper">
      <PlacesAutocomplete
        value={valueState}
        onChange={onChange}
        onSelect={handleSelect}
        searchOptions={searchOptions}
        onError={onError}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <div>
            <div
              className={`location-search-wrapper ${
                readOnly ? "disabled" : ""
              }`}
            >
              <textarea
                {...(getInputProps({
                  placeholder: placeholder || t("autocomplete.placeholder"),
                  className: "location-search-input",
                  type: "search",
                  onBlur: onBlur,
                  onFocus: onFocus,
                }) as any)}
                disabled={readOnly ? true : false}
                autoFocus={autoFocus}
              />
            </div>
            <div className="autocomplete-dropdown-container">
              {loading ? (
                <div className="loading-wrapper">
                  {t("autocomplete.loading")}
                </div>
              ) : (
                suggestions?.map((suggestion: any, i: number) => {
                  const className = "list-label";
                  const key = `${suggestion?.placeId}${i}`;
                  return (
                    <div
                      {...getSuggestionItemProps(suggestion, {
                        className,
                        key,
                      })}
                      key={key}
                    >
                      <span>{suggestion.description}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </PlacesAutocomplete>
    </div>
  );
};
